import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import {
  autoUpdate,
  FloatingPortal,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useMergeRefs
} from '@floating-ui/react'
import {
  faMagnifyingGlass,
  faTableColumns
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import {
  type ChangeEvent,
  type ReactElement,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { TextInput } from '~/components/Form/Input/TextInput'
import { Button } from '~/components/Global/Interactions/Button/Button'
import { ButtonVariation } from '~/components/Global/Interactions/Button/ButtonVariation'
import { InlineOnboarding } from '~/components/Global/Onboarding/InlineOnboarding'
import { useFloatingTransition } from '~/hooks/useFloatingTransition'
import { themeClass } from '~/styles/theme.css'
import {
  columnPickerPopover,
  columnPickerSectionTitle,
  columnPickerTrigger
} from './DataTable.css'
import type {
  ColumnPickerDropEdge,
  ColumnPickerZone,
  DataTableColumnPickerItem,
  DataTableColumnPin
} from './DataTableColumnPickerRow'
import DataTableColumnPickerZoneSection from './DataTableColumnPickerZoneSection'

export type {
  ColumnPickerDropEdge,
  ColumnPickerZone,
  DataTableColumnPickerItem,
  DataTableColumnPin
} from './DataTableColumnPickerRow'

type DataTableColumnPickerProps = {
  columns: DataTableColumnPickerItem[]
  onToggle: (columnId: string) => void
  onPinChange: (columnId: string, pin: DataTableColumnPin) => void
  onReorder: (
    columnId: string,
    toZone: ColumnPickerZone,
    targetColumnId: string,
    edge: ColumnPickerDropEdge
  ) => void
}

const DataTableColumnPicker = ({
  columns,
  onToggle,
  onPinChange,
  onReorder
}: DataTableColumnPickerProps): ReactElement => {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const onReorderRef = useRef(onReorder)
  onReorderRef.current = onReorder
  const triggerRef = useRef<HTMLSpanElement>(null)

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: (next) => {
      setOpen(next)
      if (!next) setSearch('')
    },
    strategy: 'fixed',
    placement: 'top-start',
    middleware: [offset(6), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate
  })

  const mergedTriggerRef = useMergeRefs([refs.setReference, triggerRef])

  const click = useClick(context)
  const dismiss = useDismiss(context)
  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss
  ])
  const { isMounted, styles: transitionStyles } = useFloatingTransition(
    context,
    { slideFrom: 'above', duration: 350 }
  )

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return columns
    return columns.filter((c) => c.label.toLowerCase().includes(q))
  }, [columns, search])

  const dragDisabled = search.trim().length > 0
  const leftItems = filtered.filter((c) => c.pin === 'left')
  const middleItems = filtered.filter((c) => c.pin === false)
  const rightItems = filtered.filter((c) => c.pin === 'right')

  useEffect(() => {
    return monitorForElements({
      canMonitor: ({ source }) => source.data.type === 'data-table-column',
      onDrop: ({ source, location }) => {
        const target = location.current.dropTargets[0]
        if (!target) return
        const columnId = source.data.columnId
        const targetColumnId = target.data.columnId
        const toZone = target.data.zone
        const edge = target.data.edge
        if (
          typeof columnId !== 'string' ||
          typeof targetColumnId !== 'string' ||
          (toZone !== 'left' && toZone !== 'middle' && toZone !== 'right') ||
          (edge !== 'top' && edge !== 'bottom')
        ) {
          return
        }
        if (columnId === targetColumnId) return
        onReorderRef.current(columnId, toZone, targetColumnId, edge)
      }
    })
  }, [])

  return (
    <>
      <span
        ref={mergedTriggerRef}
        className={columnPickerTrigger}
        {...getReferenceProps()}
      >
        <Button
          type="button"
          buttonSize="small"
          variation={ButtonVariation.Secondary}
          narrow
          title="Colonnes"
        >
          <FontAwesomeIcon icon={faTableColumns} />
        </Button>
      </span>
      <InlineOnboarding
        title="Personnalisez vos colonnes"
        details="Affichez ou masquez les colonnes, réordonnez-les par glisser-déposer et épinglez-les à gauche ou à droite du tableau. Vos choix sont conservés."
        anchorRef={triggerRef}
        configName="onboarding.column_picker_onboarding"
        show={!open}
        align="end"
      />
      <FloatingPortal>
        {isMounted && (
          <div
            ref={refs.setFloating}
            style={{ ...floatingStyles, zIndex: 99 }}
            {...getFloatingProps()}
          >
            <div
              className={clsx(themeClass, columnPickerPopover)}
              style={transitionStyles}
            >
              <TextInput
                standalone
                inputSize="small"
                placeholder="Rechercher une colonne…"
                value={search}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setSearch(e.target.value)
                }
                leadingIcon={faMagnifyingGlass}
                autoFocus
              />
              <DataTableColumnPickerZoneSection
                title="Épinglées à gauche"
                zone="left"
                items={leftItems}
                dragDisabled={dragDisabled}
                onToggle={onToggle}
                onPinChange={onPinChange}
              />
              <DataTableColumnPickerZoneSection
                title="Colonnes"
                zone="middle"
                items={middleItems}
                dragDisabled={dragDisabled}
                onToggle={onToggle}
                onPinChange={onPinChange}
              />
              <DataTableColumnPickerZoneSection
                title="Épinglées à droite"
                zone="right"
                items={rightItems}
                dragDisabled={dragDisabled}
                onToggle={onToggle}
                onPinChange={onPinChange}
              />
              {filtered.length === 0 && (
                <div className={columnPickerSectionTitle}>Aucun résultat</div>
              )}
            </div>
          </div>
        )}
      </FloatingPortal>
    </>
  )
}

export default DataTableColumnPicker
